import { useCallback } from 'react'
import { AuthenticationModel, ClientAddress, ClientModel } from '@/domain/models'
import { authenticationState, localStorageState, jwtAdapterAtom } from '@/presentation/components'
import { useRecoilState, useRecoilValue } from 'recoil'

type SavedUser = {
  username: string
  saveUsername: boolean
}

type AuthProps = {
  getCurrentClient: () => string
  getClient: () => ClientModel
  getSaveUser: () => SavedUser
  saveUser: (user: SavedUser) => void
  setClient: (client: ClientModel) => void
  login: (token: AuthenticationModel) => void
  logout: () => void
  getAddress: (adressCode: number) => ClientAddress | undefined
}

export const ADMIN_CODE = 999999
export const KEY_ACCESS_TOKEN = 'access_token'
export const KEY_SAVE_USER = 'save_user'

export const useAuth = (): AuthProps => {
  const [autentication, setAuthentication] = useRecoilState(authenticationState)
  const localStorageAdapter = useRecoilValue(localStorageState)
  const jwtAdapter = useRecoilValue(jwtAdapterAtom)

  const login = (accessToken: AuthenticationModel): void => {
    localStorageAdapter.set(KEY_ACCESS_TOKEN, accessToken)
    setAuthentication(currentState => ({
      ...currentState,
      accessToken,
      registerLogin: true
    }))
  }

  const logout = useCallback((): void => {
    localStorageAdapter.set(KEY_ACCESS_TOKEN, undefined)
    setAuthentication({
      client: null as unknown as ClientModel,
      accessToken: null as unknown as AuthenticationModel,
      registerLogin: false
    })
  }, [])

  const getCurrentClient = useCallback((): string => {
    const accessToken = localStorageAdapter.get(KEY_ACCESS_TOKEN)
    const tokenDecode = jwtAdapter.decode(accessToken?.access_token)
    return tokenDecode?.sub as string
  }, [])

  const getClient = useCallback((): ClientModel => {
    const accessToken = localStorageAdapter.get(KEY_ACCESS_TOKEN)

    if (accessToken?.access_token) {
      const accessTokenDecode = jwtAdapter.decode(accessToken.access_token)
      const codeClient = accessTokenDecode.sub as string
      if (codeClient.toUpperCase() === 'ADMIN') {
        return {
          ...null as unknown as ClientModel,
          codCliente: ADMIN_CODE,
          nomeCliente: codeClient.toUpperCase()
        }
      } else if (codeClient?.endsWith('@insidesistemas.com.br')) {
        return {
          ...null as unknown as ClientModel,
          codCliente: ADMIN_CODE,
          nomeCliente: accessTokenDecode?.nome,
          email: accessTokenDecode?.sub
        }
      } else if (isNaN(parseInt(codeClient))) {
        return {
          ...null as unknown as ClientModel,
          codCliente: ADMIN_CODE,
          nomeCliente: accessTokenDecode?.name
        }
      } else {
        return autentication.client
      }
    }

    return autentication.client
  }, [autentication.client])

  const setClient = useCallback((client: ClientModel): void => {
    setAuthentication(currentState => ({ ...currentState, client }))
  }, [])

  const getAddress = (adressCode: number): ClientAddress | undefined => {
    const client = getClient()
    const adresss = client.enderecoAlternativo?.find(address => address.codInterno === adressCode)

    return adresss
  }

  const getSaveUser = (): SavedUser => {
    let savedUser = localStorageAdapter.get(KEY_SAVE_USER) as SavedUser

    if (!savedUser) {
      savedUser = {
        username: '',
        saveUsername: false
      }
    }

    return savedUser
  }

  const saveUser = (user: SavedUser): void => {
    localStorageAdapter.set(KEY_SAVE_USER, user)
  }

  return {
    login,
    logout,
    setClient,
    getCurrentClient,
    getClient,
    getAddress,
    getSaveUser,
    saveUser
  }
}
